import React from "react";
import { QUALITY_COLOR, emptyParts } from "@/lib/quality";

/**
 * Generic 4-legged animal stick figure — based on the user's brush sketch.
 *
 * Anatomy mapping (one continuous figure):
 *   • Ears   – the upside-down V at the top of the head
 *   • Head   – the short stub jutting down-left from where the ears meet
 *   • Neck   – vertical line from the ear/head junction down to the back
 *   • Back   – horizontal line connecting front shoulder to rear hip
 *   • Front  – inverted V at the left shoulder
 *   • Rear   – inverted V at the right hip
 *   • Tail   – single diagonal line from the back/rear junction upward
 */
export const AnimalSymbol = ({ parts = emptyParts(), size = 100, className = "" }) => {
  const c = (k) => QUALITY_COLOR[parts?.[k]] || QUALITY_COLOR.unknown;
  const SW = 9; // base stroke width

  // Anchor points
  const EAR_JUNCTION = { x: 72, y: 30 };  // tip where the two ears meet
  const NECK_TOP = EAR_JUNCTION;
  const SHOULDER = { x: 72, y: 86 };      // back-front + neck meet
  const HIP = { x: 160, y: 86 };          // back-rear + tail meet

  return (
    <svg
      viewBox="0 0 200 160"
      width={size}
      height={size * (160 / 200)}
      className={className}
      data-testid="animal-symbol"
      aria-label="animal symbol"
    >
      {/* EARS – V on top of the head */}
      <g data-part="ears" stroke={c("ears")} strokeWidth={SW} strokeLinecap="round">
        <line x1={EAR_JUNCTION.x} y1={EAR_JUNCTION.y} x2="54" y2="4" />
        <line x1={EAR_JUNCTION.x} y1={EAR_JUNCTION.y} x2="90" y2="4" />
      </g>

      {/* HEAD – short stub jutting down-left from the ear junction */}
      <line
        data-part="head"
        x1={EAR_JUNCTION.x} y1={EAR_JUNCTION.y} x2="44" y2="54"
        stroke={c("head")} strokeWidth={SW} strokeLinecap="round"
      />

      {/* NECK – vertical line from the ear/head junction to the back */}
      <line
        data-part="neck"
        x1={NECK_TOP.x} y1={NECK_TOP.y} x2={SHOULDER.x} y2={SHOULDER.y}
        stroke={c("neck")} strokeWidth={SW + 1} strokeLinecap="round"
      />

      {/* BACK – horizontal body line */}
      <line
        data-part="back"
        x1={SHOULDER.x} y1={SHOULDER.y} x2={HIP.x} y2={HIP.y}
        stroke={c("back")} strokeWidth={SW} strokeLinecap="round"
      />

      {/* FRONT legs – inverted V at the shoulder */}
      <g data-part="front" stroke={c("front")} strokeWidth={SW} strokeLinecap="round">
        <line x1={SHOULDER.x} y1="88" x2="60" y2="148" />
        <line x1={SHOULDER.x} y1="88" x2="84" y2="148" />
      </g>

      {/* REAR legs – inverted V at the hip */}
      <g data-part="rear" stroke={c("rear")} strokeWidth={SW} strokeLinecap="round">
        <line x1={HIP.x} y1="88" x2="148" y2="148" />
        <line x1={HIP.x} y1="88" x2="172" y2="148" />
      </g>

      {/* TAIL – single diagonal line meeting back & rear at the hip */}
      <line
        data-part="tail"
        x1={HIP.x} y1={HIP.y} x2="184" y2="54"
        stroke={c("tail")} strokeWidth={SW - 1} strokeLinecap="round"
      />
    </svg>
  );
};

export default AnimalSymbol;
